import { useState } from 'react';
import Select from 'react-select';
import { Switch } from '../../../../components/ui/switch';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

export const BUSINESSHOURS = Array.from({ length: 96 }, (_, index) => {
  const hour = Math.floor(index / 4);
  const minutes = (index % 4) * 15;
  const period = hour < 12 ? 'AM' : 'PM';
  const displayHour = hour % 12 || 12;

  return {
    value: hour + minutes / 60,
    label: `${displayHour}:${String(minutes).padStart(2, '0')} ${period}`
  };
});

const customSelectStyle = {
  control: (provided) => ({
    ...provided,
    width: '100%',
    minWidth: '88px',
    minHeight: '28px',
    cursor: 'pointer',
    border: '1px solid #e1e5e9',
    borderRadius: '4px',
    boxShadow: 'none',
    backgroundColor: '#f8fafc',
    '&:hover': {
      borderColor: '#3b5998',
      backgroundColor: '#ffffff'
    }
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: '160px',
    border: '1px solid #e1e5e9',
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
  }),
  valueContainer: (provided) => ({
    ...provided,
    fontSize: '11px',
    padding: '4px 6px'
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#3b5998'
      : state.isFocused
      ? '#f1f5f9'
      : 'white',
    color: state.isSelected ? 'white' : '#374151',
    cursor: 'pointer',
    padding: '6px 8px',
    fontSize: '11px'
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 1000,
    width: '100%'
  })
};

const Schedule = ({
  dayName,
  values,
  setFieldValue,
  errors,
  touched,
  dayIndex,
  hasProPlan
}) => {
  const currentValue = values?.businessHours[dayName.toLowerCase()];
  const isClosed = !currentValue || currentValue === '/';
  const [start, end] =
    !isClosed && currentValue ? currentValue.split('/') : ['9', '18'];

  const startValue =
    BUSINESSHOURS.find((bh) => bh.value === Number(start)) ||
    BUSINESSHOURS.find((bh) => bh.value === 9);
  const endValue =
    BUSINESSHOURS.find((bh) => bh.value === Number(end)) ||
    BUSINESSHOURS.find((bh) => bh.value === 18);

  const handleSwitch = (checked) => {
    if (checked) {
      setFieldValue(`businessHours.${dayName.toLowerCase()}`, '9/18');
    } else {
      setFieldValue(`businessHours.${dayName.toLowerCase()}`, '/');
    }
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 py-2">
      <div className="w-full sm:w-20 flex items-center gap-2 shrink-0">
        <Switch
          checked={!isClosed}
          onCheckedChange={handleSwitch}
          disabled={!hasProPlan}
        />
        <span className="text-xs font-medium">{dayName.slice(0, 3)}</span>
      </div>
      {!isClosed ? (
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-1 w-full">
          <div className="w-full sm:w-auto sm:min-w-[88px]">
            <Select
              options={BUSINESSHOURS}
              isSearchable={false}
              value={startValue}
              onChange={(selectedOption) => {
                setFieldValue(
                  `businessHours.${dayName.toLowerCase()}`,
                  `${selectedOption.value}/${endValue.value}`
                );
              }}
              styles={customSelectStyle}
              placeholder="Open"
              menuPlacement="auto"
              isDisabled={!hasProPlan}
            />
          </div>
          <span className="text-xs text-gray-400">to</span>
          <div className="w-full sm:w-auto sm:min-w-[88px]">
            <Select
              options={BUSINESSHOURS}
              isSearchable={false}
              value={endValue}
              onChange={(selectedOption) => {
                setFieldValue(
                  `businessHours.${dayName.toLowerCase()}`,
                  `${startValue.value}/${selectedOption.value}`
                );
              }}
              styles={customSelectStyle}
              placeholder="Close"
              menuPlacement="auto"
              isDisabled={!hasProPlan}
            />
          </div>
        </div>
      ) : (
        <span className="text-xs text-gray-400">Closed</span>
      )}
    </div>
  );
};

const BusinessHours = ({
  values,
  setFieldValue,
  errors,
  touched,
  hasProPlan = true
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const days = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday'
  ];

  const openDays = days.filter(
    (day) =>
      values?.businessHours[day.toLowerCase()] &&
      values?.businessHours[day.toLowerCase()] !== '/'
  ).length;
  let summaryText = '';
  if (openDays === 0) summaryText = 'Closed';
  else if (openDays === 7) summaryText = 'Open 7 days';
  else summaryText = `${openDays} days open`;

  return (
    <div className="text-base sm:text-lg bg-white min-w-0">
      <div
        className="h-12 sm:h-14 rounded-xl border-2 flex items-center justify-between px-4 cursor-pointer select-none"
        onClick={() => setIsExpanded((v) => !v)}>
        <div className="flex flex-col justify-center h-full min-w-0">
          <span className="text-sm">{summaryText}</span>
        </div>
        <div className="ml-2 flex items-center h-full shrink-0">
          {isExpanded ? (
            <ChevronUpIcon className="h-5 w-4" />
          ) : (
            <ChevronDownIcon className="h-5 w-4" />
          )}
        </div>
      </div>
      {isExpanded && (
        <div className="p-3 sm:p-4 pt-6 sm:pt-8 bg-white">
          <p className="text-sm font-medium mb-2">Set Business Hours</p>
          <div className="space-y-1">
            {days.map((day, index) => (
              <Schedule
                key={day}
                dayName={day}
                values={values}
                setFieldValue={setFieldValue}
                errors={errors}
                touched={touched}
                dayIndex={index}
                hasProPlan={hasProPlan}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessHours;
